/**
 * Staff accounts and the Arc Connect Provider Portal (R-734, revised by R-753).
 *
 * STAFF = platform admin, OR at least one org membership that is active. A membership
 * counts when `membership_status` is "active", `org_status` is NOT "suspended" or
 * "inactive", AND the org's `application_status` is NOT "rejected". This is the SAME rule
 * as the portal's mirror (`portalAudience.ts`); the two must agree. `empty` (an org awaiting
 * activation) counts, so self-signup admins can finish onboarding in the portal; a rejected
 * self-signup also keeps org_status `empty`, which is why its application status is read.
 * A missing `org_status` or `application_status` does not block (older kernels send no
 * `application_status`). Everyone else is a patient, because every account is a patient by
 * default.
 *
 * ⭐ OWNER RULINGS 2026-09-28 (R-753), binding:
 *   - Staff CHOOSE; they are not forced out of the patient app. A signed-in staff account
 *     gets ONE pop-up ("You also have access to the Arc Connect Provider Portal") with
 *     "Go to Provider Portal" / "Stay in Patient App" (StaffPortalPrompt). "Stay" is
 *     remembered for the session (sessionStorage on web, Preferences until the next sign-in
 *     on native). A "Switch to Provider Portal" menu item stays available to staff.
 *   - Arriving from the portal's "Switch to Patient App" (`?from=portal`) IS that choice.
 *   - Share invites finish first: while a caregiver/follower invite token is pending
 *     (Preferences `invitation_token`, or `?param1=` in the URL) the pop-up does not show.
 *
 * ⚠️ `profile.role` is NOT the signal and must not become it; it is unreliable. The kernel's
 * `GET /me/capabilities` is the source (it is on the consent-guard allowlist, so it answers
 * even while consents are outstanding).
 *
 * ⚠️ FAILURE MEANS "PATIENT". If capabilities cannot be read, the account is treated as not
 * staff: no pop-up, no menu item.
 */
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Preferences } from "@capacitor/preferences";
import { kernelClient, getPortalUrl } from "./api";

export type CapabilityMembership = {
  org_id?: number;
  role?: string;
  org_type?: string | null;
  org_status?: string;
  /** Kernel R-753: the org's signup-application status. Absent on older kernels. */
  application_status?: string | null;
  membership_status?: string;
};

export type KernelCapabilities = {
  memberships?: CapabilityMembership[] | null;
  is_platform_admin?: boolean;
};

/** A capabilities read that has not answered in this long is treated as a failure. */
export const CAPABILITIES_TIMEOUT_MS = 8000;

/** Org statuses that stop a membership counting. Must match the portal's rule (R-733). */
export const BLOCKING_ORG_STATUSES: readonly string[] = ["suspended", "inactive"];

/** Org application statuses that stop a membership counting (R-753). Mirrors the portal. */
export const BLOCKING_APPLICATION_STATUSES: readonly string[] = ["rejected"];

const isOneOf = (list: readonly string[], value: unknown): boolean =>
  typeof value === "string" && list.includes(value.toLowerCase());

const isActive = (value: unknown): boolean =>
  typeof value === "string" && value.toLowerCase() === "active";

/** The staff rule. Pure; `null`/garbage input is "not staff". */
export const isStaffAccount = (caps: KernelCapabilities | null | undefined): boolean => {
  if (!caps || typeof caps !== "object") return false;
  if (caps.is_platform_admin === true) return true;
  const memberships = Array.isArray(caps.memberships) ? caps.memberships : [];
  // `membership_status` defaults to "active" in the kernel schema, so a missing value is
  // active. `org_status` and `application_status` block only on a listed value; a missing
  // value does not block (matches the portal).
  return memberships.some(
    (m) =>
      !!m &&
      isActive(m.membership_status ?? "active") &&
      !isOneOf(BLOCKING_ORG_STATUSES, m.org_status) &&
      !isOneOf(BLOCKING_APPLICATION_STATUSES, m.application_status),
  );
};

/** `GET /me/capabilities`, bounded so a hung kernel cannot hold up sign-in or restore. */
export const fetchKernelCapabilities = async (): Promise<KernelCapabilities> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error("capabilities request timed out")),
      CAPABILITIES_TIMEOUT_MS,
    );
  });
  try {
    const response = await Promise.race([
      kernelClient.get<KernelCapabilities>("/me/capabilities"),
      timeout,
    ]);
    return response?.data;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const isWeb = () => Capacitor.getPlatform() === "web";

// ── "Stay in Patient App", remembered for the session ─────────────────────────

export const PORTAL_CHOICE_KEY = "arc.portalChoice";
const STAY = "patient-app";

const safeSession = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* storage unavailable: the pop-up may be asked again on reload, nothing worse */
    }
  },
  remove(key: string) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** Remember "stay in the patient app" until the next sign-in (web: this tab's session). */
export const rememberPatientAppChoice = async (): Promise<void> => {
  if (isWeb()) {
    safeSession.set(PORTAL_CHOICE_KEY, STAY);
    return;
  }
  await Preferences.set({ key: PORTAL_CHOICE_KEY, value: STAY });
};

export const hasChosenPatientApp = async (): Promise<boolean> => {
  if (isWeb()) return safeSession.get(PORTAL_CHOICE_KEY) === STAY;
  return (await Preferences.get({ key: PORTAL_CHOICE_KEY })).value === STAY;
};

export const clearPortalChoice = async (): Promise<void> => {
  if (isWeb()) {
    safeSession.remove(PORTAL_CHOICE_KEY);
    return;
  }
  await Preferences.remove({ key: PORTAL_CHOICE_KEY });
};

// ── `?from=portal`: the person chose the patient app in the portal ────────────

export const APP_SWITCH_PARAM = "from";
export const FROM_PORTAL = "portal";
export const FROM_APP = "app";

/**
 * The URL at bundle load. Captured here, synchronously, because the router's first
 * `<Redirect>` rewrites `window.location` during the first render (see App.tsx
 * `AppUrlListener`), before any effect could read the query.
 */
const LAUNCH_SEARCH = typeof window !== "undefined" ? window.location.search : "";

let arrivedFromPortal = false;

/**
 * Web, once at start-up: if the page was opened by the portal's "Switch to Patient App"
 * (`?from=portal`), record the choice and strip the marker from the address bar so a reload
 * or a shared link does not carry it. Returns true when the marker was present.
 */
export const consumeFromPortalMarker = async (launchSearch: string = LAUNCH_SEARCH): Promise<boolean> => {
  if (!isWeb()) return false;
  const fromLaunch = new URLSearchParams(launchSearch).get(APP_SWITCH_PARAM) === FROM_PORTAL;
  const current = new URL(window.location.href);
  const fromCurrent = current.searchParams.get(APP_SWITCH_PARAM) === FROM_PORTAL;
  if (fromCurrent) {
    current.searchParams.delete(APP_SWITCH_PARAM);
    try {
      window.history.replaceState(window.history.state, "", current.pathname + current.search + current.hash);
    } catch {
      /* cosmetic only */
    }
  }
  if (!fromLaunch && !fromCurrent) return false;
  arrivedFromPortal = true;
  await rememberPatientAppChoice();
  return true;
};

/**
 * A fresh sign-in starts a new session: forget any earlier "stay" — unless this page load
 * came from the portal's "Switch to Patient App", which is the person's choice for it.
 */
export const resetPortalChoiceForNewSession = async (): Promise<void> => {
  await clearPortalChoice();
  if (arrivedFromPortal) await rememberPatientAppChoice();
};

/** Test hook: forget the module's record of a `?from=portal` launch. */
export const __resetFromPortalForTests = () => {
  arrivedFromPortal = false;
};

// ── Share invites finish first (owner ruling 1) ───────────────────────────────

export const INVITATION_TOKEN_KEY = "invitation_token";
export const INVITATION_TOKEN_CHANGED_EVENT = "arc:invitation-token-changed";

let launchInvitePending = new URLSearchParams(LAUNCH_SEARCH).has("param1");

/**
 * Call after `invitation_token` is written or removed. Wakes StaffPortalPrompt to re-check,
 * and ends the launch-URL fallback: from here on Preferences is the record.
 */
export const notifyInvitationTokenChanged = () => {
  launchInvitePending = false;
  try {
    window.dispatchEvent(new CustomEvent(INVITATION_TOKEN_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
};

/** True while a data-share invite is waiting: stored token, or `?param1=` in the URL. */
export const hasPendingInvitation = async (): Promise<boolean> => {
  if (launchInvitePending) return true;
  try {
    if (new URLSearchParams(window.location.search).has("param1")) return true;
  } catch {
    /* ignore */
  }
  const { value } = await Preferences.get({ key: INVITATION_TOKEN_KEY });
  return !!value;
};

/** Test hook: reset the launch-URL invite fallback. */
export const __setLaunchInvitePendingForTests = (value: boolean) => {
  launchInvitePending = value;
};

// ── Going to the portal ───────────────────────────────────────────────────────

/** `url` with `key=value` added to its query, before any `#fragment`. */
export const withQueryParam = (url: string, key: string, value: string): string => {
  const hashAt = url.indexOf("#");
  const base = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const sep = base.includes("?") ? (base.endsWith("?") || base.endsWith("&") ? "" : "&") : "?";
  return `${base}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}${hash}`;
};

/** Label of the staff-only menu item (Settings, SideNav). Mirrors the portal's "Switch to Patient App". */
export const SWITCH_TO_PORTAL_LABEL = "Switch to Provider Portal";

/** The "Switch to Provider Portal" menu item's target: `getPortalUrl()` marked `?from=app`. */
export const portalSwitchUrl = (): string => withQueryParam(getPortalUrl(), APP_SWITCH_PARAM, FROM_APP);

/** Native: open the portal in the system browser. */
export const openPortalInSystemBrowser = async (url: string): Promise<void> => {
  try {
    await Browser.open({ url });
  } catch (error) {
    console.warn("Browser plugin could not open the portal; falling back", error);
    window.open(url, "_system");
  }
};

/** Web: a full-page navigation (Back returns to the patient app). */
export const navigateToPortal = (url: string) => {
  window.location.assign(url);
};

/** Go to the portal the platform's way: full-page navigation on web, system browser on native. */
export const goToProviderPortal = async (url: string): Promise<void> => {
  if (isWeb()) {
    navigateToPortal(url);
    return;
  }
  await openPortalInSystemBrowser(url);
};
